import { useMemo } from "react";
import dayjs from "dayjs";
import type { SummaryCostEstimateTrendsResponse, SummaryProductTrendsResponse } from "../types";

const roundTo2 = (n: number) => Math.round(n * 100) / 100;

const calculatePercentageChange = (current: number, previous: number): number | null => {
  if (previous === 0) return null;
  return Math.round(((current - previous) / Math.abs(previous)) * 10000) / 100;
};

interface PastChartData {
  chartData: Record<string, number | string | null>[];
  groups: string[];
  productsByGroup: Record<string, string[]>;
}

// Pivots the category-driven trends response into per-period chart rows. In
// `category=total` mode bars are keyed by group; in `category=<X>` mode bars
// are keyed by `product_<X>` and the per-period category total is surfaced
// under the group key. `groups` and `productsByGroup[group]` follow BE
// `response.products` order verbatim.
export const useDatabricksPastChartData = (
  data: SummaryProductTrendsResponse | undefined,
  selectedCategory: string | null
): PastChartData =>
  useMemo(() => {
    if (!data?.trends.length) {
      return { chartData: [], groups: [], productsByGroup: {} };
    }

    const isCategoryMode = Boolean(selectedCategory);
    const groups = isCategoryMode ? [] : data.products;
    const productsByGroup: Record<string, string[]> = {};
    if (selectedCategory) productsByGroup[selectedCategory] = data.products;

    const periodMap = new Map<string, Record<string, number>>();
    for (const item of data.trends) {
      if (!periodMap.has(item.period)) periodMap.set(item.period, {});
      const row = periodMap.get(item.period)!;
      const key = isCategoryMode ? `product_${item.product}` : item.product;
      row[key] = (row[key] ?? 0) + item.estimated_cost;
    }

    const sortedRows = Array.from(periodMap.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([period, raw]) => {
        const rounded: Record<string, number | string> = { period };
        for (const key of Object.keys(raw)) rounded[key] = roundTo2(raw[key]);
        return rounded;
      });

    const sumKeys = (row: Record<string, number | string>, keys: string[]) =>
      keys.reduce((acc, key) => acc + ((row[key] as number) ?? 0), 0);

    const chartData = sortedRows.map((row, index) => {
      const result: Record<string, number | string | null> = { ...row };
      const prev = index > 0 ? sortedRows[index - 1] : null;

      if (isCategoryMode) {
        const productKeys = productsByGroup[selectedCategory!].map(p => `product_${p}`);
        const total = sumKeys(row, productKeys);
        result[selectedCategory!] = roundTo2(total);
        if (prev) {
          result[`percentage_${selectedCategory!}`] = calculatePercentageChange(
            total,
            sumKeys(prev, productKeys)
          );
          for (const product of productsByGroup[selectedCategory!]) {
            const key = `product_${product}`;
            result[`percentage_${product}`] = calculatePercentageChange(
              (row[key] as number) ?? 0,
              (prev[key] as number) ?? 0
            );
          }
        }
      } else {
        const total = sumKeys(row, groups);
        result.total = roundTo2(total);
        if (prev) {
          result.percentage_total = calculatePercentageChange(total, sumKeys(prev, groups));
          for (const group of groups) {
            result[`percentage_${group}`] = calculatePercentageChange(
              (row[group] as number) ?? 0,
              (prev[group] as number) ?? 0
            );
          }
        }
      }

      return result;
    });

    return { chartData, groups, productsByGroup };
  }, [data, selectedCategory]);

interface FutureChartData {
  chartData: Record<string, number | string | boolean | null>[];
  segments: string[];
}

/**
 * Pivots the segment-list cost-estimate graph into per-period chart rows keyed
 * by segment. For the current month the projected portion is split off
 * (`estimate − actual`) so the lighter bar stacks on top of the actual.
 * `segments` follows the BE `products` legend order verbatim.
 */
export const useDatabricksFutureChartData = (
  data: SummaryCostEstimateTrendsResponse | undefined
): FutureChartData =>
  useMemo(() => {
    if (!data?.graph.length) return { chartData: [], segments: [] };

    const chartData = data.graph.map(item => {
      const isCurrentMonth = dayjs().isSame(item.date, "month");
      const row: Record<string, number | string | boolean | null> = {
        period: item.date,
        total: item.total,
        is_estimate: item.is_estimate,
        percentage_total: item.percentage_total ?? null,
        total_estimate: isCurrentMonth
          ? Math.max(0, item.total_estimate - item.total)
          : item.total_estimate,
      };
      for (const seg of item.segments) {
        row[seg.product] = seg.cost;
        row[`${seg.product}_estimate`] = isCurrentMonth
          ? Math.max(0, seg.cost_estimate - seg.cost)
          : seg.cost_estimate;
        row[`percentage_${seg.product}`] = seg.percentage_change ?? null;
      }
      return row;
    });

    return { chartData, segments: data.products };
  }, [data]);
